import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/motion";
import { Breadcrumbs } from "../components/Breadcrumbs";
import NotFound from "./NotFound";
import { POSTS, postBySlug } from "../data/blog";
import { SITE, url, breadcrumbSchema } from "../data/site";

export default function BlogPost() {
  const { slug } = useParams();
  const post = postBySlug(slug);
  if (!post) return <NotFound />;

  const path = `/blog/${slug}`;
  const trail = [["Home", "/"], ["Resources", "/blog"], [post.title, path]];
  const related = POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  const articleSchema = {
    "@context": "https://schema.org", "@type": "Article",
    headline: post.title, description: post.description,
    datePublished: post.date, dateModified: post.date,
    mainEntityOfPage: url(path),
    author: { "@type": "Person", name: "Sreenivas Palla", jobTitle: "CPA & CA", worksFor: { "@id": url("/#org") } },
    publisher: { "@id": url("/#org") },
  };

  return (
    <>
      <Seo title={`${post.title} | ABSLpay`} description={post.description} path={path} type="article"
        jsonLd={[articleSchema, breadcrumbSchema(trail.map(([n, p]) => [n === post.title ? post.title : n, p]))]} />
      <Breadcrumbs trail={[["Home", "/"], ["Resources", "/blog"], [post.category, path]]} />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-20 lg:pt-14">
        <div className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.15em]">
          <span className="text-emerald-700">{post.category}</span>
          <span className="text-slate-300">&middot;</span>
          <time dateTime={post.date} className="text-slate-400">
            {new Date(post.date + "T00:00:00").toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
          </time>
          <span className="text-slate-300">&middot;</span>
          <span className="text-slate-400">{post.readTime}</span>
        </div>
        <h1 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1] text-slate-900" data-testid="blog-post-title">
          {post.title}
        </h1>
        <p className="mt-5 text-sm text-slate-500">By {SITE.founder} &middot; Founder, {SITE.brand}</p>

        <div className="mt-10 space-y-8">
          {post.sections.map((s, i) => (
            <Reveal key={i} y={16}>
              <section>
                {s.h && <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-slate-900 mb-3">{s.h}</h2>}
                {(s.p || []).map((para, j) => (
                  <p key={j} className="text-base leading-relaxed text-slate-600 mb-4">{para}</p>
                ))}
                {s.list && (
                  <ul className="list-disc pl-6 space-y-2 text-base leading-relaxed text-slate-600">
                    {s.list.map((li) => <li key={li}>{li}</li>)}
                  </ul>
                )}
                {s.table && (
                  <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white mt-2">
                    <table className="w-full min-w-[480px] text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 text-left">
                          {s.table.headers.map((h) => <th key={h} className="p-3.5 font-bold text-slate-900">{h}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {s.table.rows.map((row, r) => (
                          <tr key={r} className="border-b border-slate-100 last:border-0">
                            {row.map((cell, c) => (
                              <td key={c} className={`p-3.5 ${c === 0 ? "font-semibold text-slate-800" : "text-slate-600"}`}>{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-14 rounded-2xl bg-slate-950 p-8 text-center">
            <h2 className="font-display text-xl font-bold text-white">Want a CPA handling this for you?</h2>
            <p className="mt-2 text-sm text-slate-400">Payroll, tax filing and compliance in all 50 states, from $39/month.</p>
            <Link to="/contact" data-testid="blog-post-cta" className="mt-5 inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white font-bold px-6 py-3 hover:bg-emerald-500 transition-colors">
              Get a free quote <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>
      </article>

      <section className="bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="font-display text-xl font-bold text-slate-900">Keep reading</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="group rounded-2xl border border-slate-200 p-6 transition-colors hover:border-emerald-400">
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-emerald-700">{p.category}</span>
                <h3 className="mt-3 font-display text-base font-bold leading-snug text-slate-900 group-hover:text-emerald-800 transition-colors">{p.title}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
